import { BackendGrade, Grade } from './Grade';
import { Group, GroupStudent } from './Group';
import { PageType } from './Trajectory';

export type ScoreType = ScoreDifficulty | PageType;
export type AdditionalScoreData = Record<ScoreType, string>;

export type AdditionalScores = {
  averageChapterScore: AdditionalScoreData;
  trajectoryStudentScoreOfType: Record<string, AdditionalScoreData>;
};

export type ScoreDifficulty = 'easy' | 'average' | 'hard';

export type ScoreData = Record<string, Score[]>;

export type Score = {
  score: number;
  maxScore: number;
  difficulty: ScoreType;
};

export type StudentWithScores = {
  id: string;
  firstName: string;
  name: string;
  scores: ScoreData;
  isOutdated: boolean;
  additionalScores: AdditionalScores;
};

export type GroupWithScores = Omit<Group, 'grade' | 'schoolSubjects'> & {
  students: StudentWithScores[];
  isOutdated: boolean;
  additionalScores: AdditionalScores;
};

export type GradeWithScores = Grade & {
  groups: GroupWithScores[];
};

export type GroupStudentSeriesScores = {
  isOutdated: boolean;
  students: Record<string, StudentSeriesScores>;
  scores: Record<string, SeriesScores>;
};
export type StudentSeriesScores = {
  scores: Record<string, SeriesScores>;
  student: {
    name: string;
    surname: string;
  };
};

export type SeriesScores = Record<string, ExerciseScores>;
export type ExerciseScores = {
  score: number;
  maxScore: number;
  isAnswered?: boolean;
};

// BACKEND

export type BackendScoreData = Record<string, BackendScore[]>;

export type BackendScore = Omit<Score, 'maxScore'> & {
  max_score: number;
};

export type BackendStudentWithScores = Omit<
  StudentWithScores,
  'scores' | 'additionalScores' | 'isOutdated'
> & {
  scores: {
    data: BackendScoreData;
  };
  is_outdated: boolean;
  additional_scores: AdditionalScores;
};

export type BackendGroupWithScores = Omit<
  GroupWithScores,
  'isDefault' | 'averageScores' | 'isOutdated' | 'additionalScores'
> & {
  is_default: 1 | 0;
  students: {
    data: BackendStudentWithScores[];
  };
  is_outdated: boolean;
  additional_scores: AdditionalScores;
};

export type BackendGradeWithScores = BackendGrade & {
  groups: {
    data: BackendGroupWithScores[];
  };
};

export type BackendStudentSeriesScores = {
  scores: Record<string, BackendSeriesScores>;
  student: {
    name: string;
    surname: string;
  };
};

export type BackendSeriesScores = Record<string, BackendExerciseScores>;
export type BackendExerciseScores = {
  score: number;
  max_score: number;
  is_answered?: boolean;
};

export type UserTrajectoryScores = {
  isOutdated: boolean;
  results: Record<string, SeriesScores>;
};

export type ProgressScore = {
  backgroundColor: string;
  score: number;
  scoreLevel: 'low' | 'medium' | 'high';
  barColor?: string;
};

export type AttemptType = 'best' | 'first' | 'last';
export type OrderingType = 'leerling' | 'opbouw' | 'theorie' | 'oefeningen';

export type ProgressScoreWithSequences = ProgressScore & { sequences: Array<ExerciseSequence> };
export type AnswerableProgressScore = ProgressScore & { isAnswerable: boolean };
export type ProgressScoreWithExercises = ProgressScore & {
  exercises: Array<AnswerableProgressScore>;
  isCompleted?: boolean;
};

export type ExerciseSequence = {
  sequenceName: string;
  average: ProgressScore;
  exercises: Array<AnswerableProgressScore>;
};

type GroupStudentBackend = {
  id: string;
  firstName: string;
  lastName: string;
};

export type StudentScore = {
  theory: {
    isCompleted?: boolean;
    backgroundColor?: string;
    textColor?: string;
  };
  opbouw: ProgressScoreWithExercises;
  exercises: ProgressScoreWithSequences;
};

export type StudentProfileScore = {
  student: GroupStudent;
  group?: StudentScoreGroup;
  score: StudentScore;
};

export type StudentScoreGroup = {
  id: string;
  name: string;
};

type StudentProfileScoreBackend = {
  student: GroupStudentBackend;
  group?: StudentScoreGroup;
  score: StudentScore;
};

export type StudentScoreListSummary = {
  exercises?: ProgressScore;
  opbouw?: ProgressScore;
  theory?: ProgressScore;
};

export type StudentScoreVersion = {
  id: string;
  title: string;
  groups: StudentScoreGroup[];
  students: StudentProfileScore[];
};

export type StudentScoreVersionBackEnd = {
  id: string;
  title: string;
  groups: StudentScoreGroup[];
  students: StudentProfileScoreBackend[];
};

export type StudentScoreListBackend = {
  summary: StudentScoreListSummary;
  versions: StudentScoreVersionBackEnd[];
};

export type StudentScoreList = {
  summary: StudentScoreListSummary;
  versions: StudentScoreVersion[];
};

// MAPPER

const mapBackendScoreStudent = (
  backendStudent: StudentProfileScoreBackend
): StudentProfileScore => {
  return {
    student: {
      id: backendStudent.student.id,
      firstName: backendStudent.student.firstName,
      name: backendStudent.student.lastName,
    },
    group: backendStudent.group,
    score: backendStudent.score,
  };
};

export const mapDashPayload = (scoringData: {
  data: StudentScoreListBackend;
}): StudentScoreList => {
  return {
    summary: scoringData.data.summary,
    versions: scoringData.data.versions.map(mapScoreVersion),
  };
};

const mapScoreVersion = (backendVersion: StudentScoreVersionBackEnd): StudentScoreVersion => ({
  id: backendVersion.id,
  title: backendVersion.title,
  groups: backendVersion.groups,
  students: backendVersion.students.map(mapBackendScoreStudent),
});

const mapScoreData = (backendAverageScores: BackendScoreData): ScoreData =>
  Object.keys(backendAverageScores).reduce(
    (agg, objKey) => ({
      ...agg,
      [objKey]: backendAverageScores[objKey].map(mapBackendScoreToScore),
    }),
    {}
  );

const mapBackendScoreToScore = (data: BackendScore): Score => ({
  score: data.score,
  maxScore: data.max_score,
  difficulty: data.difficulty,
});

const mapBackendStudentWithScoresToStudentWithScores = (
  data: BackendStudentWithScores
): StudentWithScores => ({
  id: data.id,
  firstName: data.firstName,
  name: data.name,
  scores: mapScoreData(data.scores.data),
  isOutdated: data.is_outdated,
  additionalScores: !!data.additional_scores
    ? data.additional_scores
    : ({
        averageChapterScore: {},
        trajectoryStudentScoreOfType: {},
      } as AdditionalScores),
});

const mapBackendGroupWithScoresToGroupWithScores = (
  data: BackendGroupWithScores
): GroupWithScores => ({
  id: data.id,
  name: data.name,
  description: data.description,
  isDefault: Boolean(data.is_default),
  students: data.students.data.map(mapBackendStudentWithScoresToStudentWithScores),
  isOutdated: data.is_outdated,
  additionalScores: data.additional_scores,
});

export const getGroupScoresByChapterMapper = (response: any): GroupWithScores =>
  mapBackendGroupWithScoresToGroupWithScores(response.data);

export const getUserScoresByChapterMapper = (response: any): StudentWithScores =>
  mapBackendStudentWithScoresToStudentWithScores(response.data);

export const mapBackendExerciseScoresToExerciseScores = (
  data: BackendExerciseScores
): ExerciseScores => ({
  score: data.score,
  maxScore: data.max_score,
  isAnswered: data.is_answered,
});

export const mapBackendSeriesScoresToSeriesScores = (data: BackendSeriesScores): SeriesScores =>
  Object.keys(data).reduce(
    (agg, objKey) => ({
      ...agg,
      [objKey]: mapBackendExerciseScoresToExerciseScores(data[objKey]),
    }),
    {}
  );

export const getTrajectoryScoresByUserMapper = (response: any): UserTrajectoryScores | undefined =>
  response.data
    ? {
        isOutdated: false,
        results: Object.keys(response.data).reduce(
          (agg, objKey) =>
            objKey === 'is_outdated'
              ? agg
              : {
                  ...agg,
                  [objKey]: mapBackendSeriesScoresToSeriesScores(response.data[objKey]),
                },
          {}
        ),
      }
    : undefined;

export const mapBackendStudentSeriesScoresToStudentSeriesScores = (
  data: BackendStudentSeriesScores
): StudentSeriesScores => ({
  scores: Object.keys(data.scores).reduce(
    (agg, objKey) => ({
      ...agg,
      [objKey]: mapBackendSeriesScoresToSeriesScores(data.scores[objKey]),
    }),
    {}
  ),
  student: data.student,
});

export const getTrajectoryScoresByGroupMapper = (response: any): GroupStudentSeriesScores => ({
  isOutdated: response.data.is_outdated || false,
  students: response.data.students
    ? Object.keys(response.data.students).reduce(
        (agg, objKey) => ({
          ...agg,
          [objKey]: mapBackendStudentSeriesScoresToStudentSeriesScores(
            response.data.students[objKey]
          ),
        }),
        {}
      )
    : {},
  scores: response.data.scores
    ? Object.keys(response.data.scores).reduce(
        (agg, objKey) => ({
          ...agg,
          [objKey]: mapBackendSeriesScoresToSeriesScores(response.data.scores[objKey]),
        }),
        {}
      )
    : {},
});
